import { useState, useEffect, useContext } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  BrandKitGQL,
  BrandKitBrandsGQL,
  ConsoleAPIGraph,
  CreativeHubTypes,
} from '@adflow/graphqlgen';
import { AdTemplate, FontFace } from '@adflow/types';
import { canEdit } from '../permissions';
import { TRPCClient } from '../apiClient';
import { parseFonts } from '@adflow/utils';
import { UserContext } from '@adflow/utils';
import { generateUserToCheck } from '../../../utils/user-context';

/**
 * Custom hook for fetching and storing AdTemplate, Brand, Fonts, and user permissions.
 */
export function useAdTemplateData(id: string) {
  const apolloClient = useApolloClient();
  const userContext = useContext(UserContext);
  const [adTemplate, setAdTemplate] = useState<AdTemplate | null>(null);
  const [brand, setBrand] = useState<ConsoleAPIGraph.Brand | null>(null);
  const [brandKit, setBrandKit] = useState<CreativeHubTypes.Brand | null>(null);
  const [fonts, setFonts] = useState<FontFace[]>([]);
  const [fontsReady, setFontsReady] = useState(false);

  const [warningOpen, setWarningOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  const [hasEditPermissions, setHasEditPermissions] = useState(false);

  /**
   * 1) Check user edit permissions
   * Uses generateUserToCheck to support both UserContext and cookie-based auth (screenshot service)
   */
  useEffect(() => {
    const checkPermissions = async () => {
      const userToCheck = generateUserToCheck(userContext?.user || null);
      if (!userToCheck) {
        setHasEditPermissions(false);
        return;
      }
      const canUserEdit = await canEdit(userToCheck);
      setHasEditPermissions(canUserEdit);
    };
    checkPermissions();
  }, [userContext?.user]);

  /**
   * 2) Fetch the template, then its fonts, and only then mark the editor ready.
   * EditorProvider snapshots defaultStoreValues once, so opening early leaves
   * customFonts empty after the brand-kit response arrives.
   */
  useEffect(() => {
    let cancelled = false;

    async function fetchBrand(brandId: string) {
      try {
        const data = await TRPCClient.client.consoleAPIGraph.getBrand.query(brandId);
        if (!data) {
          throw new Error('No data returned for brandId=' + brandId);
        }
        if (cancelled) return;
        setBrand(data);

        if (!data.team) {
          setFonts([]);
          return;
        }
        const fontData = await TRPCClient.client.consoleAPIGraph.listFonts.query({
          team: data.team,
        });
        if (cancelled) return;
        setFonts(fontData?.fontFaces ?? []);
      } catch (error) {
        console.error('Error fetching brand:', error);
      }
    }

    async function fetchBrandKitFonts(advertiserId: string) {
      try {
        const { error, data } = await apolloClient.query({
          query: BrandKitGQL.GET_ALL_FONTS_BY_OWNER,
          variables: { advertiserId },
        });
        if (cancelled) return;
        if (error) {
          console.error('Error fetching fonts from BrandKit:', error);
          setWarningMessage(
            'We encountered an issue while fetching fonts from the Brand Kit. ' +
              "You can continue editing, but won't have access to Brand Kit fonts. " +
              'Please try again or contact support if the issue persists.',
          );
          setWarningOpen(true);
          return;
        }
        const newFonts = data?.getFontFamiliesByOwner
          ? parseFonts(data.getFontFamiliesByOwner)
          : [];
        setFonts(newFonts);
      } catch (error) {
        console.error('Error fetching BrandKit fonts:', error);
      }
    }

    async function fetchBrandKit(brandKitId: string) {
      try {
        const { data, error } = await apolloClient.query({
          query: BrandKitBrandsGQL.GET_BRAND_BY_ID,
          variables: { id: brandKitId },
          fetchPolicy: 'network-only',
        });
        if (cancelled) return;
        if (error) {
          console.error('Error fetching brand kit:', error);
          return;
        }
        if (data?.getBrandById) {
          setBrandKit(data.getBrandById);
        }
      } catch (error) {
        console.error('Error fetching brand kit:', error);
      }
    }

    async function load() {
      if (!adTemplate) {
        try {
          const data = await TRPCClient.client.adTemplate.findOne.query({ id });
          if (!cancelled) setAdTemplate(data);
        } catch (error) {
          console.error('Error fetching ad template:', error);
        }
        return;
      }

      const isOffTheShelfTemplate = adTemplate.figmaComponentId;
      try {
        const pending: Promise<void>[] = [];
        if (adTemplate.brandId && !isOffTheShelfTemplate) {
          pending.push(fetchBrand(adTemplate.brandId));
        } else if (adTemplate.businessEntityId) {
          pending.push(fetchBrandKitFonts(JSON.stringify(adTemplate.businessEntityId)));
        }
        if (adTemplate.brandKitId) {
          pending.push(fetchBrandKit(adTemplate.brandKitId));
        }
        await Promise.all(pending);
      } finally {
        if (!cancelled) setFontsReady(true);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [apolloClient, id, adTemplate]);

  const ready = Boolean(adTemplate) && fontsReady;

  return {
    adTemplate,
    brand,
    brandKit,
    fonts,
    ready,
    warningOpen,
    warningMessage,
    setWarningOpen,
    setWarningMessage,
    hasEditPermissions,
  };
}
